import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { PushPipe } from '@ngrx/component';
import { asyncScheduler, BehaviorSubject, delay, filter, lastValueFrom, observeOn } from 'rxjs';

import { DrawerResizeDirective } from '../../directives/drawer-resize.directive';
import { DrawerContainerWidthConfig, DrawerState } from '../../types';
import { DrawerDragHandleComponent } from '../drawer-drag-handle/drawer-drag-handle.component';
import { DrawerComponent } from '../drawer/drawer.component';

@Component({
  selector: 'rt-drawer-container',
  templateUrl: './drawer-container.component.html',
  styleUrls: ['./drawer-container.component.less'],
  imports: [PushPipe, DrawerResizeDirective, DrawerComponent, DrawerDragHandleComponent],
  standalone: true,
})
export class DrawerContainerComponent {
  public static animationTime: number = 300;

  @Input() public widthConfig: DrawerContainerWidthConfig;

  @Output() public drawerSizeChanges: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('overlayTemplate', { static: true }) public overlayTemplate: TemplateRef<any>;
  public drawerState$: BehaviorSubject<DrawerState> = new BehaviorSubject(DrawerState.Close);

  // Slide-in animation should only execute after the component is drawn on the overlay - hence the asyncScheduler.
  public visibilityTransition$ = this.drawerState$.pipe(observeOn(asyncScheduler));
  public DrawerState = DrawerState;

  private overlayRef: OverlayRef;

  private close$ = this.drawerState$.pipe(
    filter(() => !!this.overlayRef),
    filter(state => state === DrawerState.Close),
    delay(DrawerContainerComponent.animationTime),
  );

  private open$ = this.drawerState$.pipe(
    filter(() => !!this.overlayRef),
    filter(state => state === DrawerState.Open),
  );

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private destroyRef: DestroyRef,
  ) {}

  public ngOnInit() {
    this.createContainerOverlay();
    this.watchOverlayStateChanges();
  }

  public ngOnDestroy(): void {
    this.detachTemplatePortal();
  }

  public toggle(): Promise<DrawerState> {
    this.drawerState$.next(
      this.drawerState$.value === DrawerState.Open ? DrawerState.Close : DrawerState.Open,
    );
    return lastValueFrom(this.drawerState$);
  }

  public open(): Promise<DrawerState> {
    this.drawerState$.next(DrawerState.Open);
    return lastValueFrom(this.drawerState$);
  }

  public close(): Promise<DrawerState> {
    this.drawerState$.next(DrawerState.Close);
    return lastValueFrom(this.drawerState$);
  }

  private createContainerOverlay() {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: false,
      backdropClass: 'overlay',
      positionStrategy: this.overlay.position().global().right('0'),
    });
    this.overlayRef = this.overlay.create(overlayConfig);
  }

  private attachTemplatePortal() {
    this.overlayRef.attach(new TemplatePortal(this.overlayTemplate, this.viewContainerRef));
  }

  private detachTemplatePortal() {
    this.overlayRef.detach();
  }

  private watchOverlayStateChanges() {
    this.close$
      .pipe(
        observeOn(asyncScheduler),
        delay(DrawerContainerComponent.animationTime),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.detachTemplatePortal();
      });

    this.open$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.attachTemplatePortal();
    });
  }
}
